import React from "react"
import { Box, CircularProgress, Theme, Typography, createStyles, makeStyles } from "@material-ui/core"
import { useAppContext } from "../../../app-context/use-app-context"
import ReportedHoursTable from "../../../treegrid/reported-hours"
import { useCalendarSettings } from "../../../hooks/use-calendar-settings"
import { GetReportedHoursQuery, useGetReportedHoursQuery } from "../../api"
import { makeReportedHoursScopeQuery } from "../../api/reported-hours-query"
import DateRangeSelector from "../DateRangeSelector/DateRangeSelector"
import { formatDateRangeQuery, useDateRange } from "../DateRangeSelector/use-date-range"
import ReporterMembershipFilter from "../ReporterMembershipFilter"
import { getTableHeaderText } from "../utils"
import type { HourReportingWorkflowSettingsProps } from "../../hooks/use-hour-reporting-workflow-settings"
import { resolveDefaultMembership } from "../../resolve-membership"
import { useAuthUserMembership } from "../../../memberships/hooks/use-auth-user-membership"
import { reportedHoursGridConfigs } from "../../../treegrid/reported-hours/column-settings"
import { useLocalizedFormat } from "../../../hooks/use-localized-format"
import { useTranslation } from "react-i18next"
import { useReportedHoursMembershipOptions } from "../../hooks/use-reported-hours-membership-options"
import { useHourReportMembershipQuery } from "../../hooks/use-hour-report-membership-query"

const ReportedHours = ({ workflowSettings }: HourReportingWorkflowSettingsProps) => {
  const classes = useStyles()
  const formatDate = useLocalizedFormat()
  const { t } = useTranslation("hour-report")
  const { appContext } = useAppContext()
  const isUserContext = appContext.mainContext?.type === "user"
  const isOrganisationContext = appContext.mainContext?.type === "org"
  const { calendarSettings, isLoading: isLoadingCalendarSettings } = useCalendarSettings({
    mainContext: appContext.mainContext,
    skipAll: !appContext.mainContext,
  })
  const { fromDate, toDate, setFromDate, setToDate, setDateRange } = useDateRange()
  const {
    membership: authUserMembership,
    canReportOthersHoursAnywhere,
    canReportOthersHoursAsMember,
  } = useAuthUserMembership()
  const canReportOthersHours = Boolean(canReportOthersHoursAnywhere || canReportOthersHoursAsMember)
  const defaultMembershipId =
    resolveDefaultMembership({
      subContext: appContext.subContext,
      authUserMembership,
      canReportOthersHours,
    })?.id || null
  const orgId = appContext.mainContext?.type === "org" ? appContext.mainContext.id : ""
  const { memberships, isLoading: isLoadingMemberships } = useReportedHoursMembershipOptions({
    orgId,
    canReportOthersHours,
  })
  const { selectedMembershipId, setSelectedMembershipId } = useHourReportMembershipQuery({
    defaultMembershipId,
    membershipIds: memberships.map(({ id }) => id),
    isLoading: isLoadingMemberships,
    allowEmpty: true,
  })
  const queryParams = useReportedHoursQueryParams({ fromDate, toDate, appContext, membershipId: selectedMembershipId })
  const { data: reports = [], isLoading: isLoadingReports } = useGetReportedHoursQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  })
  const selectedMembership = memberships.find((membership) => membership.id === selectedMembershipId)
  const headerText = getTableHeaderText({
    appContext,
    fromDate,
    toDate,
    selectedMembershipName: selectedMembership?.fullname,
    formatDate,
    t,
  })

  return (
    <Box>
      <Box display="flex" alignItems="stretch" flexWrap="wrap" style={{ gap: 16 }}>
        <DateRangeSelector
          fromDate={fromDate}
          toDate={toDate}
          firstDayOfWeek={calendarSettings.firstDayOfWeek}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
          onDateRangeChange={setDateRange}
        />
        {!isUserContext ? (
          <ReporterMembershipFilter
            memberships={memberships}
            selectedMembershipId={selectedMembershipId}
            isLoading={isLoadingMemberships}
            onChange={setSelectedMembershipId}
          />
        ) : null}
      </Box>

      <Typography variant="h6" style={{ marginBottom: 16 }}>
        {headerText}
      </Typography>

      <Box className={classes.tableContainer}>
        {isLoadingReports || isLoadingCalendarSettings ? (
          <Box className={classes.loadingOverlay}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <ReportedHoursTable
            reports={reports}
            dateFormat={calendarSettings.dateFnsDateFormat}
            dateSeparator={calendarSettings.dateFormatSpecs.dateSeparator}
            firstDayOfWeek={calendarSettings.firstDayOfWeek}
            gridInfo={headerText}
            workflowSettings={workflowSettings}
            gridConfig={
              isOrganisationContext ? reportedHoursGridConfigs.organisation : reportedHoursGridConfigs.personal
            }
          />
        )}
      </Box>
    </Box>
  )
}

const useReportedHoursQueryParams = ({
  fromDate,
  toDate,
  appContext,
  membershipId,
}: UseReportedHoursQueryParamsOptions) => {
  return React.useMemo(() => {
    const queryParams: GetReportedHoursQuery = {
      fromDate: formatDateRangeQuery(fromDate),
      toDate: formatDateRangeQuery(toDate),
      ...makeReportedHoursScopeQuery({ appContext, membershipId }),
    }

    return queryParams
  }, [appContext, fromDate, membershipId, toDate])
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dateRangePanel: {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
      maxWidth: 520,
    },
    dateRangeShortcuts: {
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(-1),
    },
    dateRangeShortcut: {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    tableContainer: {
      position: "relative",
    },
    loadingOverlay: {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: theme.palette.background.paper,
      opacity: 0.7,
    },
  })
)

type UseReportedHoursQueryParamsOptions = {
  fromDate: Date
  toDate: Date
  appContext: ReturnType<typeof useAppContext>["appContext"]
  membershipId: string | null
}

export default ReportedHours
